import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface RelationsAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  disabled: boolean;
}

export default function RelationsAccordion({
  control,
  errors,
  disabled,
  watch,
}: RelationsAccordionProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'relations',
  });

  const menuItems = [
    // { value: 'admin_assistant', label: 'Admin Assistant' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'brother', label: 'Brother' },
    { value: 'child', label: 'Child' },
    { value: 'custom', label: 'Custom' },
    { value: 'domestic_partner', label: 'Domestic Partner' },
    // { value: 'dotted_line_manager', label: 'Dotted Line Manager' },
    // { value: 'exec_assistant', label: 'Executive Assistant' },
    { value: 'father', label: 'Father' },
    { value: 'friend', label: 'Friend' },
    { value: 'manager', label: 'Manager' },
    { value: 'mother', label: 'Mother' },
    { value: 'parent', label: 'Parent' },
    { value: 'partner', label: 'Partner' },
    { value: 'referred_by', label: 'Referred By' },
    { value: 'relative', label: 'Relative' },
    { value: 'sister', label: 'Sister' },
    { value: 'spouse', label: 'Spouse' },
  ];

  const relationsErrors = errors.relations;
  const relationsCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({ type: '', customType: '', value: '' });
  };

  return (
    <SharedProfileInformationAccordion
      title="Relations"
      count={relationsCount}
      hasError={!!relationsErrors}
      onAdd={handleOnAdd}
      disabled={disabled}
    >
      {fields.map((item, index) => {
        const typeField = watch(`relations.${index}.type`);

        return (
          <Box key={item.id}>
            <Stack
              gap={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ my: 1.5 }}
            >
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`relations.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      inputProps={{
                        id: `relations-type-${index}`,
                      }}
                      InputLabelProps={{
                        htmlFor: `relations-type-${index}`,
                      }}
                      name="relations"
                      select
                      disabled={disabled}
                      label="Type"
                      error={!!relationsErrors?.[index]?.type}
                      helperText={
                        relationsErrors?.[index]?.type
                          ? 'This field is required.'
                          : ''
                      }
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {menuItems.map((item) => (
                        <MenuItem value={item.value} key={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                {typeField === 'custom' && (
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`relations.${index}.customType`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        name="relations"
                        inputRef={field.ref}
                        inputProps={{
                          id: `relations-customType-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `relations-customType-${index}`,
                        }}
                        disabled={disabled}
                        label="Custom Type"
                        error={!!relationsErrors?.[index]?.customType}
                        helperText={
                          relationsErrors?.[index]?.customType
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 225,
                          textTransform: 'capitalize',
                        }}
                      />
                    )}
                  />
                )}
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`relations.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      name="relations"
                      inputRef={field.ref}
                      error={!!relationsErrors?.[index]?.value}
                      helperText={
                        relationsErrors?.[index]?.value
                          ? 'This field is required.'
                          : ''
                      }
                      disabled={disabled}
                      autoComplete="off"
                      label="Value"
                      sx={{
                        minWidth: 225,
                      }}
                    />
                  )}
                />
              </Stack>

              <SharedProfileInformationItemActions
                index={index}
                totalCount={relationsCount}
                disabled={disabled}
                onMoveDown={() => move(index, index + 1)}
                onMoveUp={() => move(index, index - 1)}
                onDelete={() => remove(index)}
              />
            </Stack>
            {index !== fields.length - 1 && <Divider />}
          </Box>
        );
      })}
    </SharedProfileInformationAccordion>
  );
}
